import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categories } from '@/mocks/articles';

function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return views.toString();
}

const categoryLabelMap: Record<string, string> = {
  star: '明星八卦',
  variety: '综艺热瓜',
  'internet-celebrity': '网红爆料',
  expose: '黑料实锤',
  drama: '影视吃瓜',
  trending: '今日热搜',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-950">
          <Navbar />
          <main className="pt-20 w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto py-20 text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-2xl bg-background-800">
                <i className="ri-file-unknow-line text-3xl text-foreground-500"></i>
              </div>
              <h1 className="text-2xl font-bold text-foreground-200 mb-2">文章未找到</h1>
              <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const categoryName = categoryLabelMap[article.category] || article.category;
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: categoryName, to: `/category/${article.category}` },
    { label: article.title },
  ];

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-950">
        <Navbar />
        <main className="pt-20 w-full px-4 md:px-6">
          <article className="max-w-4xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                {article.isHot && (
                  <span className="px-2 py-0.5 text-xs font-semibold bg-primary-500 text-background-50 rounded whitespace-nowrap">
                    热门
                  </span>
                )}
                {article.isNew && (
                  <span className="px-2 py-0.5 text-xs font-semibold bg-accent-500 text-background-950 rounded whitespace-nowrap">
                    新瓜
                  </span>
                )}
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {categoryName}
                </Link>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-100 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatViews(article.views)} 阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-message-3-line"></i>
                  {article.comments} 评论
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  阅读 {article.readTime}
                </span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="relative rounded-xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full aspect-video object-cover"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose prose-invert max-w-none mb-10 text-foreground-300 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2 mb-10 pt-6 border-t border-background-800/50">
              <span className="text-xs text-foreground-500 whitespace-nowrap">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs rounded-full bg-background-800 text-foreground-400 border border-background-700/50 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Share */}
            <div className="flex items-center gap-3 mb-10">
              <span className="text-sm text-foreground-500 whitespace-nowrap">分享到：</span>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-800 text-foreground-400 hover:text-primary-400 hover:bg-background-700 transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-800 text-foreground-400 hover:text-primary-400 hover:bg-background-700 transition-colors cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-800 text-foreground-400 hover:text-primary-400 hover:bg-background-700 transition-colors cursor-pointer" aria-label="复制链接">
                <i className="ri-link"></i>
              </button>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="max-w-7xl mx-auto pb-14">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                <h2 className="text-lg font-bold text-foreground-100">相关推荐</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <ArticleCard key={related.id} article={related} />
                ))}
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}